import { useState, useRef, useCallback, useMemo } from "react";
import { useSEO } from "@/hooks/use-seo";
import { useAuth } from "@/hooks/use-auth";
import { useLocation } from "wouter";
import { useTown } from "@/hooks/use-town";
import { TownSelect } from "@/components/TownSelect";
import {
  useListCommunityPosts,
  useCreateCommunityPost,
  useDeleteCommunityPost,
  useCreateCommunityComment,
  useListCommunityComments,
} from "@workspace/api-client-react";
import { Heart, MessageCircle, Trash2, Edit3, X, Send, Users, Search, Bookmark, BookmarkCheck, Flag } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { ImageUpload } from "@/components/ImageUpload";
import { ReportModal } from "@/components/ReportModal";
import { WebAvatar } from "@/components/WebAvatar";
import { QueryState } from "@/components/QueryState";

function FeedSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="bg-white rounded-2xl p-4 animate-pulse space-y-3">
          <div className="flex items-center gap-3"><div className="w-10 h-10 rounded-full bg-slate-200" /><div className="space-y-1.5"><div className="h-3 bg-slate-200 rounded w-24" /><div className="h-2.5 bg-slate-200 rounded w-16" /></div></div>
          <div className="h-3 bg-slate-200 rounded w-full" /><div className="h-3 bg-slate-200 rounded w-2/3" />
        </div>
      ))}
    </div>
  );
}

function AvatarImg({ url, name, sizeCls, textCls }: { url: string; name: string; sizeCls: string; textCls: string }) {
  const [err, setErr] = useState(false);
  if (!err) {
    return <img src={url} alt={name} className={`${sizeCls} rounded-full object-cover`} onError={() => setErr(true)} />;
  }
  return <span className={`text-primary font-bold ${textCls}`}>{(name ?? "?").charAt(0).toUpperCase()}</span>;
}

const REACTIONS = [
  { type: "love",  emoji: "❤️",  label: "Love"  },
  { type: "like",  emoji: "👍",  label: "Like"  },
  { type: "laugh", emoji: "😂",  label: "Haha"  },
  { type: "wow",   emoji: "😮",  label: "Wow"   },
  { type: "sad",   emoji: "😢",  label: "Sad"   },
  { type: "angry", emoji: "😡",  label: "Angry" },
] as const;
type ReactionType = typeof REACTIONS[number]["type"];

function reactionEmoji(type: string) {
  return REACTIONS.find((r) => r.type === type)?.emoji ?? "❤️";
}

async function sendReact(postId: number, reactionType: string | null): Promise<{ myReaction: string | null; reactions: Record<string, number> }> {
  const token = localStorage.getItem("ayrshire_token");
  if (!reactionType) return { myReaction: null, reactions: {} };
  const res = await fetch(`/api/community/posts/${postId}/react`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ reactionType }),
  });
  return res.json() as Promise<{ myReaction: string | null; reactions: Record<string, number> }>;
}

async function sendCommentReact(commentId: number, reactionType: string): Promise<{ myReaction: string | null; reactions: Record<string, number> }> {
  const token = localStorage.getItem("ayrshire_token");
  const res = await fetch(`/api/community/comments/${commentId}/react`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ reactionType }),
  });
  return res.json() as Promise<{ myReaction: string | null; reactions: Record<string, number> }>;
}

// ─── Types ────────────────────────────────────────────────────────────────────
interface PostData {
  id: number;
  content: string;
  town?: string | null;
  userName?: string | null;
  userAvatarUrl?: string | null;
  userIsModerator?: boolean | null;
  imageUrl?: string | null;
  likesCount: number;
  commentsCount: number;
  reactions: Record<string, number>;
  myReaction?: string | null;
  likedByMe?: boolean;
  createdAt?: string;
  userId?: number;
  savedId?: number | null;
}

async function sendStar(postId: number, savedId: number | null): Promise<number | null> {
  const token = localStorage.getItem("ayrshire_token");
  if (savedId !== null) {
    await fetch(`/api/saved/${savedId}`, {
      method: "DELETE",
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    return null;
  }
  const res = await fetch("/api/saved", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ itemType: "community_post", itemId: postId }),
  });
  const data = await res.json() as { id: number };
  return data.id;
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function Community() {
  useSEO({ title: "Community Board", description: "Join the conversation with residents across Ayrshire. Share updates, ask questions and connect with your local community in Ayr, Kilmarnock, Irvine and beyond." });
  const { user } = useAuth();
  const { selectedTown } = useTown();
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const [search, setSearch] = useState("");
  const [myPostsOnly, setMyPostsOnly] = useState(false);
  const [savedFilter, setSavedFilter] = useState(false);
  const [sort, setSort] = useState<"recent" | "popular">("recent");
  const [composerOpen, setComposerOpen] = useState(false);
  const [commentPostId, setCommentPostId] = useState<number | null>(null);
  const [reactionOverrides, setReactionOverrides] = useState<Record<number, { myReaction: string | null; reactions: Record<string, number> }>>({});
  const [savedOverrides, setSavedOverrides] = useState<Record<number, number | null>>({});

  // Moderator check
  const canModerate = user?.role === "admin" || (user as { isModerator?: boolean } | null)?.isModerator === true;

  const queryTown = selectedTown === "All Towns" ? undefined : selectedTown;
  const { data: rawPosts = [], isLoading, isError, refetch } = useListCommunityPosts(
    queryTown ? { town: queryTown } : undefined
  );

  const { mutateAsync: deletePost } = useDeleteCommunityPost();

  const posts = useMemo(() => {
    let list = (rawPosts as unknown as PostData[]).map((p) => ({
      ...p,
      ...(reactionOverrides[p.id] ?? {}),
      savedId: savedOverrides[p.id] !== undefined ? savedOverrides[p.id] : (p.savedId ?? null),
    }));

    if (myPostsOnly && user) list = list.filter((p) => p.userId === user.id);
    if (savedFilter) list = list.filter((p) => p.savedId !== null);

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((p) => p.content.toLowerCase().includes(q) || (p.userName ?? "").toLowerCase().includes(q));
    }

    if (sort === "popular") {
      list = [...list].sort((a, b) => {
        const aTotal = Object.values(a.reactions ?? {}).reduce((s, n) => s + n, 0);
        const bTotal = Object.values(b.reactions ?? {}).reduce((s, n) => s + n, 0);
        return bTotal - aTotal;
      });
    }
    return list;
  }, [rawPosts, reactionOverrides, savedOverrides, savedFilter, myPostsOnly, user, search, sort]);

  const handleReact = useCallback(async (postId: number, reactionType: string) => {
    if (!user) { setLocation("/login"); return; }
    const post = (rawPosts as unknown as PostData[]).find((p) => p.id === postId);
    const current = reactionOverrides[postId]?.myReaction ?? post?.myReaction ?? null;
    const prev = { ...(reactionOverrides[postId] ?? { myReaction: post?.myReaction ?? null, reactions: post?.reactions ?? {} }) };
    const newReactions = { ...prev.reactions };
    if (current) newReactions[current] = Math.max(0, (newReactions[current] ?? 1) - 1);
    const newMyReaction = current === reactionType ? null : reactionType;
    if (newMyReaction) newReactions[newMyReaction] = (newReactions[newMyReaction] ?? 0) + 1;
    setReactionOverrides((r) => ({ ...r, [postId]: { myReaction: newMyReaction, reactions: newReactions } }));
    try {
      const result = await sendReact(postId, reactionType);
      setReactionOverrides((r) => ({ ...r, [postId]: result }));
    } catch {
      setReactionOverrides((r) => ({ ...r, [postId]: prev }));
    }
  }, [user, rawPosts, reactionOverrides, setLocation]);

  const handleDelete = async (postId: number) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await deletePost({ id: postId });
      refetch();
    } catch {
      toast({ title: "Error", description: "Could not delete post.", variant: "destructive", action: <ToastAction altText="Try again" onClick={() => void handleDelete(postId)}>Try again</ToastAction> });
    }
  };

  const handleStar = useCallback(async (postId: number) => {
    if (!user) { setLocation("/login"); return; }
    const post = (rawPosts as unknown as PostData[]).find((p) => p.id === postId);
    const currentSavedId = savedOverrides[postId] !== undefined ? savedOverrides[postId] : (post?.savedId ?? null);
    setSavedOverrides((s) => ({ ...s, [postId]: currentSavedId === null ? -1 : null }));
    try {
      const newSavedId = await sendStar(postId, currentSavedId);
      setSavedOverrides((s) => ({ ...s, [postId]: newSavedId }));
    } catch {
      setSavedOverrides((s) => ({ ...s, [postId]: currentSavedId }));
    }
  }, [user, rawPosts, savedOverrides, setLocation]);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-primary px-4 pt-6 pb-0">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-white font-bold text-2xl">Community</h1>
              <p className="text-white/70 text-sm">Chat with your neighbours</p>
            </div>
            {user ? (
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <span className="text-white font-bold text-sm">{user.name.charAt(0).toUpperCase()}</span>
              </div>
            ) : (
              <button onClick={() => setLocation("/login")} className="text-white/80 text-sm hover:text-white">Sign in</button>
            )}
          </div>

          {/* Search */}
          <div className="relative mb-3">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50 pointer-events-none" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search posts…"
              className="w-full bg-white/10 border border-white/20 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder:text-white/50 outline-none focus:bg-white/15 transition-colors"
            />
          </div>

          {/* Filter tabs */}
          {user && (
            <div className="flex gap-2 mb-3 flex-wrap">
              <button
                onClick={() => { setMyPostsOnly(false); setSavedFilter(false); }}
                className="px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors"
                style={{ background: !myPostsOnly && !savedFilter ? "#f0a500" : "rgba(255,255,255,0.15)", color: "#fff" }}
              >All Posts</button>
              <button
                onClick={() => { setMyPostsOnly(true); setSavedFilter(false); }}
                className="px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors"
                style={{ background: myPostsOnly ? "#f0a500" : "rgba(255,255,255,0.15)", color: "#fff" }}
              >My Posts</button>
              <button
                onClick={() => { setSavedFilter(true); setMyPostsOnly(false); }}
                className="flex items-center gap-1 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors"
                style={{ background: savedFilter ? "#f0a500" : "rgba(255,255,255,0.15)", color: "#fff" }}
              ><BookmarkCheck size={12} />Saved</button>
              <div className="ml-auto flex gap-1">
                <button onClick={() => setSort("recent")} className="px-3 py-1.5 rounded-full text-xs font-semibold transition-colors" style={{ background: sort === "recent" ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.1)", color: "#fff" }}>Recent</button>
                <button onClick={() => setSort("popular")} className="px-3 py-1.5 rounded-full text-xs font-semibold transition-colors" style={{ background: sort === "popular" ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.1)", color: "#fff" }}>Popular</button>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Feed */}
      <div className="max-w-2xl mx-auto px-4 py-5 space-y-4">
        <QueryState
          isLoading={isLoading}
          isError={isError}
          isEmpty={posts.length === 0}
          onRetry={refetch}
          skeleton={<FeedSkeleton />}
          errorTitle="Couldn't load the community feed"
          errorBody="We couldn't reach Ayrshire Hub just now. Check your connection and try again."
          emptyIcon={<Users size={28} className="text-primary" />}
          emptyTitle={search ? "No posts match your search" : savedFilter ? "No saved posts yet" : myPostsOnly ? "You haven't posted yet" : selectedTown === "All Towns" ? "No posts yet" : `No posts in ${selectedTown} yet`}
          emptyBody={search ? "Try different keywords." : "Be the first to share something!"}
          emptyAction={search ? undefined : { label: user ? "Write First Post" : "Join to Post", onClick: () => user ? setComposerOpen(true) : setLocation("/signup") }}
        >
          {posts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              currentUserId={user?.id}
              canModerate={canModerate}
              isLoggedIn={!!user}
              onReact={(reactionType) => handleReact(post.id, reactionType)}
              onComment={() => setCommentPostId(post.id)}
              onDelete={() => handleDelete(post.id)}
              onStar={() => handleStar(post.id)}
            />
          ))}
        </QueryState>
      </div>

      {/* FAB */}
      {user && (
        <button
          onClick={() => setComposerOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-amber-500 hover:bg-amber-600 flex items-center justify-center shadow-lg transition-colors z-40"
          style={{ boxShadow: "0 4px 16px rgba(240,165,0,0.4)" }}
        >
          <Edit3 size={22} className="text-white" />
        </button>
      )}

      {!user && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-100 shadow-lg px-4 py-3 flex items-center justify-between z-40">
          <p className="text-sm font-medium text-slate-700">Join the conversation</p>
          <button onClick={() => setLocation("/signup")} className="bg-primary hover:bg-primary/90 text-white font-bold px-5 py-2.5 rounded-xl text-sm transition-colors">Sign Up Free</button>
        </div>
      )}

      {composerOpen && (
        <PostComposer
          defaultTown={selectedTown}
          onClose={() => setComposerOpen(false)}
          onPosted={() => { refetch(); setComposerOpen(false); }}
        />
      )}

      {commentPostId !== null && (
        <CommentSheet
          postId={commentPostId}
          currentUserId={user?.id}
          canModerate={canModerate}
          isLoggedIn={!!user}
          onClose={() => setCommentPostId(null)}
        />
      )}
    </div>
  );
}

// ─── Post Card ────────────────────────────────────────────────────────────────
function PostCard({ post, currentUserId, canModerate, isLoggedIn, onReact, onComment, onDelete, onStar }: {
  post: PostData;
  currentUserId?: number;
  canModerate: boolean;
  isLoggedIn: boolean;
  onReact: (type: string) => void;
  onComment: () => void;
  onDelete: () => void;
  onStar: () => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const holdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [, setLocation] = useLocation();

  const initials = (post.userName ?? "?").charAt(0).toUpperCase();
  const isOwn = !!(currentUserId && post.userId === currentUserId);
  const canDelete = isOwn || canModerate;
  const timeAgo = post.createdAt ? formatTimeAgo(post.createdAt) : "";
  const myReaction = post.myReaction ?? null;
  const reactions = post.reactions ?? {};
  const totalReactions = Object.values(reactions).reduce((s, n) => s + n, 0);

  const startHold = () => {
    holdRef.current = setTimeout(() => { setPickerOpen(true); holdRef.current = null; }, 500);
  };
  const endHold = (e: React.MouseEvent | React.TouchEvent) => {
    if (holdRef.current) {
      clearTimeout(holdRef.current);
      holdRef.current = null;
      e.preventDefault();
      onReact("love");
    }
  };
  const cancelHold = () => { if (holdRef.current) { clearTimeout(holdRef.current); holdRef.current = null; } };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-visible relative">
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          <button
            onClick={() => post.userId && setLocation(`/profile/${post.userId}`)}
            className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
          >
            {post.userAvatarUrl
              ? <AvatarImg url={post.userAvatarUrl} name={post.userName ?? "?"} sizeCls="w-full h-full" textCls="text-sm" />
              : <span className="text-primary font-bold text-sm">{initials}</span>}
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <button
                onClick={() => post.userId && setLocation(`/profile/${post.userId}`)}
                className="font-bold text-slate-800 text-sm hover:text-primary hover:underline transition-colors text-left"
              >{post.userName}</button>
              <div className="flex items-center gap-1">
                {isLoggedIn && !isOwn && (
                  <button
                    onClick={() => setReportOpen(true)}
                    className="text-slate-300 hover:text-amber-500 transition-colors p-1"
                    title="Report this post"
                  >
                    <Flag size={13} />
                  </button>
                )}
                {canDelete && (
                  <button onClick={onDelete} className="text-slate-300 hover:text-red-400 transition-colors p-1 -mr-1">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {post.town && <span className="text-xs text-slate-400">{post.town}</span>}
              {timeAgo && <><span className="text-xs text-slate-300">·</span><span className="text-xs text-slate-400">{timeAgo}</span></>}
              {post.userIsModerator && <span className="text-xs text-indigo-400 font-semibold">· mod</span>}
            </div>
          </div>
        </div>

        {/* Content */}
        <MentionText content={post.content} className="text-slate-700 text-sm leading-relaxed mb-3 block" />

        {/* Image */}
        {post.imageUrl && (
          <div className="rounded-xl overflow-hidden mb-3 bg-slate-100">
            <img src={post.imageUrl} alt="" className="w-full object-cover max-h-72" />
          </div>
        )}

        {/* Reaction summary */}
        {totalReactions > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2.5">
            {REACTIONS.filter((r) => (reactions[r.type] ?? 0) > 0).map((r) => (
              <button
                key={r.type}
                onClick={() => onReact(r.type)}
                className="flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium transition-all"
                style={{
                  background: myReaction === r.type ? "#1e3a5f12" : "#f8fafc",
                  borderColor: myReaction === r.type ? "#1e3a5f40" : "#e2e8f0",
                  color: "#475569",
                }}
              >
                <span>{r.emoji}</span>
                <span>{reactions[r.type]}</span>
              </button>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3 pt-2 border-t border-slate-50 relative">
          {/* React button – hold for picker, tap for love */}
          <div className="relative">
            <button
              onMouseDown={startHold}
              onMouseUp={endHold}
              onMouseLeave={cancelHold}
              onTouchStart={startHold}
              onTouchEnd={endHold}
              className="flex items-center gap-1.5 text-sm transition-colors select-none"
              style={{ color: myReaction ? "#dc2626" : "#94a3b8" }}
            >
              <span className="text-base leading-none">{myReaction ? reactionEmoji(myReaction) : "🤍"}</span>
              {totalReactions === 0 && <span className="text-xs font-medium">React</span>}
            </button>

            {pickerOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setPickerOpen(false)} />
                <div className="absolute bottom-8 left-0 z-50 bg-white border border-slate-200 rounded-2xl shadow-xl px-3 py-2 flex gap-2">
                  {REACTIONS.map((r) => (
                    <button
                      key={r.type}
                      onClick={() => { onReact(r.type); setPickerOpen(false); }}
                      title={r.label}
                      className="flex flex-col items-center gap-0.5 hover:scale-125 transition-transform"
                    >
                      <span className="text-xl leading-none">{r.emoji}</span>
                      <span className="text-[9px] text-slate-400 font-medium">{r.label}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <button
            onClick={onComment}
            className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-primary transition-colors"
          >
            <MessageCircle size={16} />
            <span className="font-medium">{post.commentsCount > 0 ? post.commentsCount : "Comment"}</span>
          </button>

          <button
            onClick={onStar}
            className="ml-auto flex items-center gap-1.5 text-sm transition-colors"
            style={{ color: post.savedId !== null && post.savedId !== undefined ? "#f0a500" : "#94a3b8" }}
            title={post.savedId !== null && post.savedId !== undefined ? "Remove from saved" : "Save post"}
          >
            {post.savedId !== null && post.savedId !== undefined
              ? <BookmarkCheck size={16} />
              : <Bookmark size={16} />}
          </button>
        </div>
      </div>

      {reportOpen && post.id && (
        <ReportModal targetType="community_post" targetId={post.id} onClose={() => setReportOpen(false)} />
      )}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
interface MentionUser { id: number; name: string; avatarUrl: string | null; town?: string | null }

function MentionText({ content, className }: { content: string; className?: string }) {
  const [, setLocation] = useLocation();
  const parts = content.split(/(@[\w\u00C0-\u024F'-]+(?: [\w\u00C0-\u024F'-]+)*)/g);

  const handleMentionClick = async (name: string) => {
    try {
      const res = await fetch(`/api/users/search?q=${encodeURIComponent(name)}`);
      const data = await res.json() as { items: { id: number; name: string }[] };
      const match = data.items?.find(u => u.name.toLowerCase() === name.toLowerCase()) ?? data.items?.[0];
      if (match) setLocation(`/profile/${match.id}`);
    } catch { /* ignore */ }
  };

  if (parts.length <= 1) return <span className={className}>{content}</span>;
  return (
    <span className={className}>
      {parts.map((part, i) =>
        part.startsWith("@")
          ? <button key={i} onMouseDown={e => { e.preventDefault(); e.stopPropagation(); void handleMentionClick(part.slice(1)); }} className="text-primary font-semibold hover:underline cursor-pointer">{part}</button>
          : <span key={i}>{part}</span>
      )}
    </span>
  );
}

// ─── Post Composer ────────────────────────────────────────────────────────────
function PostComposer({ defaultTown, onClose, onPosted }: { defaultTown: string; onClose: () => void; onPosted: () => void }) {
  const { user } = useAuth();
  const [content, setContent] = useState("");
  const [town, setTown] = useState(defaultTown === "All Towns" ? "Ayr" : defaultTown);
  const [imageUrl, setImageUrl] = useState("");
  const { mutateAsync: createPost, isPending } = useCreateCommunityPost();
  const { toast } = useToast();
  const [mentionSuggestions, setMentionSuggestions] = useState<MentionUser[]>([]);
  const [mentionStart, setMentionStart] = useState(-1);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mentionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  if (!user) return null;

  const handleContentChange = (e: { target: HTMLTextAreaElement; currentTarget: HTMLTextAreaElement }) => {
    const val = e.target.value;
    setContent(val);
    const cursor = e.target.selectionStart ?? val.length;
    const textBeforeCursor = val.slice(0, cursor);
    const lastAtIdx = textBeforeCursor.lastIndexOf("@");
    if (lastAtIdx >= 0) {
      const afterAt = textBeforeCursor.slice(lastAtIdx + 1);
      if (!afterAt.includes("\n") && afterAt.length >= 1) {
        setMentionStart(lastAtIdx);
        if (mentionTimerRef.current) clearTimeout(mentionTimerRef.current);
        mentionTimerRef.current = setTimeout(async () => {
          try {
            const res = await fetch(`/api/users/search?q=${encodeURIComponent(afterAt)}`);
            const data = await res.json() as { items: MentionUser[] };
            setMentionSuggestions(data.items ?? []);
          } catch { setMentionSuggestions([]); }
        }, 200);
        return;
      }
    }
    setMentionSuggestions([]);
    setMentionStart(-1);
  };

  const selectMention = (u: MentionUser) => {
    if (mentionStart < 0 || !textareaRef.current) return;
    const cursor = textareaRef.current.selectionStart ?? content.length;
    const before = content.slice(0, mentionStart);
    const after = content.slice(cursor);
    const newContent = `${before}@${u.name} ${after}`;
    setContent(newContent);
    setMentionSuggestions([]);
    setMentionStart(-1);
    textareaRef.current.focus();
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        const pos = mentionStart + u.name.length + 2;
        textareaRef.current.setSelectionRange(pos, pos);
      }
    });
  };

  const handlePost = async () => {
    if (!content.trim()) return;
    try {
      await createPost({ data: { content: content.trim(), town, imageUrl: imageUrl || undefined } });
      onPosted();
    } catch {
      toast({ title: "Error", description: "Could not post. Please try again.", variant: "destructive", action: <ToastAction altText="Try again" onClick={() => void handlePost()}>Try again</ToastAction> });
    }
  };

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl max-h-[85vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <span className="font-bold text-slate-800 text-base">New Post</span>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="flex items-center gap-3">
            <WebAvatar name={user.name} avatarUrl={user.avatarUrl} size={40} />
            <p className="font-semibold text-slate-800 text-sm">{user.name}</p>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Posting from</label>
            <TownSelect value={town} onChange={setTown} />
          </div>

          <div className="relative">
            <textarea
              ref={textareaRef}
              value={content}
              onChange={handleContentChange}
              placeholder="What's on your mind? Type @ to mention someone"
              rows={4}
              className="w-full text-slate-800 text-sm leading-relaxed outline-none resize-none placeholder:text-slate-400"
            />
            {mentionSuggestions.length > 0 && (
              <div className="absolute left-0 right-0 top-full z-50 bg-white rounded-xl border border-slate-200 shadow-lg overflow-hidden max-h-44 overflow-y-auto">
                {mentionSuggestions.map((u) => (
                  <button
                    key={u.id}
                    onMouseDown={(e) => { e.preventDefault(); selectMention(u); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-slate-50 transition-colors text-left"
                  >
                    <WebAvatar name={u.name} avatarUrl={u.avatarUrl} size={28} />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">{u.name}</p>
                      {u.town && <p className="text-xs text-slate-400 truncate">{u.town}</p>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <ImageUpload value={imageUrl} onChange={setImageUrl} />
        </div>

        <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-between">
          <span className="text-xs text-slate-400">{content.length}/500 chars</span>
          <button
            onClick={() => void handlePost()}
            disabled={!content.trim() || isPending}
            className="flex items-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-50 text-white font-bold px-5 py-2.5 rounded-xl text-sm transition-colors"
          >
            <Send size={14} />
            {isPending ? "Posting…" : "Post"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Comment Sheet ────────────────────────────────────────────────────────────
function CommentSheet({
  postId, currentUserId, canModerate, isLoggedIn, onClose
}: {
  postId: number;
  currentUserId?: number;
  canModerate: boolean;
  isLoggedIn: boolean;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const { data: rawComments = [], refetch } = useListCommunityComments(postId);
  const { mutateAsync: createComment, isPending } = useCreateCommunityComment();
  const [text, setText] = useState("");
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [reportCommentId, setReportCommentId] = useState<number | null>(null);
  const [commentReactions, setCommentReactions] = useState<Record<number, { myReaction: string | null; reactions: Record<string, number> }>>({});
  const [mentionSuggestions, setMentionSuggestions] = useState<MentionUser[]>([]);
  const [mentionStart, setMentionStart] = useState(-1);
  const commentInputRef = useRef<HTMLInputElement>(null);
  const mentionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  interface CommentData {
    id: number;
    userId?: number;
    userName?: string | null;
    userAvatarUrl?: string | null;
    content: string;
    createdAt?: string;
    reactions: Record<string, number>;
  }

  const comments = rawComments as unknown as CommentData[];

  const handleSubmit = async () => {
    if (!text.trim()) return;
    try {
      await createComment({ id: postId, data: { content: text.trim() } });
      setText("");
      refetch();
    } catch {
      toast({ title: "Could not post comment", variant: "destructive", action: <ToastAction altText="Try again" onClick={() => void handleSubmit()}>Try again</ToastAction> });
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!window.confirm("Delete this comment?")) return;
    setDeletingId(commentId);
    try {
      const token = localStorage.getItem("ayrshire_token");
      const res = await fetch(`/api/community/comments/${commentId}`, {
        method: "DELETE",
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!res.ok) throw new Error();
      refetch();
    } catch {
      toast({ title: "Could not delete comment", variant: "destructive", action: <ToastAction altText="Try again" onClick={() => void handleDeleteComment(commentId)}>Try again</ToastAction> });
    } finally {
      setDeletingId(null);
    }
  };

  const handleCommentReact = async (commentId: number, reactionType: string) => {
    if (!isLoggedIn) { setLocation("/login"); return; }
    try {
      const result = await sendCommentReact(commentId, reactionType);
      setCommentReactions(r => ({ ...r, [commentId]: result }));
    } catch {
      toast({ title: "Could not react", variant: "destructive" });
    }
  };

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl max-h-[80vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <span className="font-bold text-slate-800 text-base">Comments</span>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {comments.length === 0 && (
            <p className="text-center text-slate-400 text-sm py-8">No comments yet — be the first!</p>
          )}
          {comments.map((c) => {
            const isOwn = !!(currentUserId && c.userId === currentUserId);
            const canDeleteComment = isOwn || canModerate;
            const override = commentReactions[c.id];
            const myReaction = override?.myReaction ?? null;
            const reactions = override?.reactions ?? c.reactions ?? {};
            const totalR = Object.values(reactions).reduce((s, n) => s + n, 0);
            return (
              <div key={c.id} className="flex gap-3">
                <button
                  onClick={() => (c as {userId?: number}).userId && setLocation(`/profile/${(c as {userId?: number}).userId}`)}
                  className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                >
                  {c.userAvatarUrl
                    ? <AvatarImg url={c.userAvatarUrl} name={c.userName ?? "?"} sizeCls="w-full h-full" textCls="text-xs" />
                    : <span className="text-primary font-bold text-xs">{(c.userName ?? "?").charAt(0).toUpperCase()}</span>}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="bg-slate-50 rounded-2xl px-3 py-2">
                    <div className="flex items-center justify-between gap-1">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => (c as {userId?: number}).userId && setLocation(`/profile/${(c as {userId?: number}).userId}`)}
                          className="font-semibold text-xs text-slate-800 hover:text-primary hover:underline transition-colors"
                        >{c.userName ?? "Unknown"}</button>
                        {(c as {userIsModerator?: boolean}).userIsModerator && <span className="text-[10px] text-indigo-400 font-semibold">· mod</span>}
                      </div>
                      <div className="flex items-center gap-0.5">
                        {isLoggedIn && !isOwn && (
                          <button
                            onClick={() => setReportCommentId(c.id)}
                            className="text-slate-300 hover:text-amber-500 transition-colors p-0.5"
                            title="Report comment"
                          >
                            <Flag size={11} />
                          </button>
                        )}
                        {canDeleteComment && (
                          <button
                            onClick={() => void handleDeleteComment(c.id)}
                            disabled={deletingId === c.id}
                            className="text-slate-300 hover:text-red-400 transition-colors p-0.5 disabled:opacity-50"
                            title="Delete comment"
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    </div>
                    <MentionText content={c.content} className="text-sm text-slate-700 leading-snug mt-0.5 block" />
                  </div>
                  <div className="flex items-center gap-2 mt-1 pl-2">
                    {totalR > 0 && (
                      <div className="flex gap-1">
                        {REACTIONS.filter(r => (reactions[r.type] ?? 0) > 0).map(r => (
                          <button
                            key={r.type}
                            onClick={() => void handleCommentReact(c.id, r.type)}
                            className="flex items-center gap-0.5 text-xs text-slate-500"
                          >
                            <span>{r.emoji}</span><span>{reactions[r.type]}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    <button
                      onClick={() => void handleCommentReact(c.id, myReaction === "love" ? "love" : "love")}
                      className="text-xs text-slate-400 hover:text-red-400 transition-colors"
                    >
                      {myReaction ? reactionEmoji(myReaction) : "❤️"} React
                    </button>
                    {c.createdAt && (
                      <span className="text-xs text-slate-400">{formatTimeAgo(c.createdAt)}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {isLoggedIn ? (
          <div className="px-4 py-3 border-t border-slate-100 flex gap-2 items-center">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void handleSubmit(); } }}
              placeholder="Write a comment…"
              className="flex-1 bg-slate-100 rounded-full px-4 py-2 text-sm outline-none"
            />
            <button
              onClick={() => void handleSubmit()}
              disabled={!text.trim() || isPending}
              className="w-9 h-9 rounded-full bg-primary disabled:opacity-50 flex items-center justify-center flex-shrink-0"
            >
              <Send size={14} className="text-white" />
            </button>
          </div>
        ) : (
          <div className="px-4 py-3 border-t border-slate-100 text-center">
            <button onClick={() => setLocation("/login")} className="text-primary font-semibold text-sm">
              Sign in to comment
            </button>
          </div>
        )}
      </div>

      {reportCommentId !== null && (
        <ReportModal
          targetType="community_comment"
          targetId={reportCommentId}
          onClose={() => setReportCommentId(null)}
        />
      )}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatTimeAgo(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const secs = Math.floor((now.getTime() - d.getTime()) / 1000);
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  if (secs < 604800) return `${Math.floor(secs / 86400)}d ago`;
  return d.toLocaleDateString("en-GB");
}
